import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { ChevronLeft, FileText, Car, Clock, ChevronDown, ChevronRight, List, X } from 'lucide-react';
import { Link } from 'react-router-dom';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Extract headings from markdown for TOC
const extractHeadings = (markdown) => {
  if (!markdown) return [];
  const headingRegex = /^(#{1,3})\s+(.+)$/gm;
  const headings = [];
  let match;
  
  while ((match = headingRegex.exec(markdown)) !== null) {
    const level = match[1].length;
    const text = match[2].replace(/\*\*/g, '').replace(/\[([^\]]+)\]\([^)]+\)/g, '$1');
    const id = text.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
    headings.push({ level, text, id });
  }
  
  return headings;
};

// Generate slug for heading IDs
const slugify = (text) => {
  return text.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
};

// Custom heading component with ID for anchor links
const HeadingRenderer = ({ level, children, collapsedSections, toggleSection }) => {
  const text = typeof children === 'string' ? children : 
    React.Children.toArray(children).map(child => 
      typeof child === 'string' ? child : child?.props?.children || ''
    ).join('');
  const id = slugify(text);
  
  const HeadingTag = `h${level}`;
  const baseClasses = "scroll-mt-24 font-heading font-bold text-slate-900";
  
  const levelClasses = {
    1: "text-[28px] sm:text-[32px] mt-0 mb-6 pb-4 border-b border-slate-200",
    2: "text-[20px] sm:text-[22px] mt-10 mb-4",
    3: "text-[17px] sm:text-[18px] mt-6 mb-3"
  };

  // Only make H2 collapsible on mobile
  if (level === 2 && toggleSection) {
    const isCollapsed = collapsedSections?.[id];
    return (
      <button
        onClick={() => toggleSection(id)}
        className={`${baseClasses} ${levelClasses[level]} w-full text-left flex items-center justify-between gap-2 md:pointer-events-none group`}
        id={id}
        data-testid={`heading-${id}`}
      >
        <span>{children}</span>
        <span className="md:hidden text-slate-400 group-hover:text-slate-600 transition-colors">
          {isCollapsed ? <ChevronRight className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
        </span>
      </button>
    );
  }

  return (
    <HeadingTag id={id} className={`${baseClasses} ${levelClasses[level] || ''}`} data-testid={`heading-${id}`}>
      {children}
    </HeadingTag>
  );
};

// Table of Contents Component
const TableOfContents = ({ headings, activeId, onClose, isMobile }) => {
  const scrollToHeading = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
      if (onClose) onClose();
    }
  };

  return (
    <nav className={`${isMobile ? 'p-4' : ''}`} data-testid="table-of-contents">
      {isMobile && (
        <div className="flex items-center justify-between mb-4 pb-3 border-b border-slate-200">
          <h3 className="font-heading font-semibold text-slate-900">On this page</h3>
          <Button variant="ghost" size="icon" onClick={onClose} className="h-8 w-8">
            <X className="w-4 h-4" />
          </Button>
        </div>
      )}
      {!isMobile && (
        <h3 className="font-heading font-semibold text-slate-900 mb-4 text-sm uppercase tracking-wide">
          On this page
        </h3>
      )}
      <ul className="space-y-1">
        {headings.filter(h => h.level <= 2).map((heading, idx) => (
          <li key={idx}>
            <button
              onClick={() => scrollToHeading(heading.id)}
              className={`
                w-full text-left text-sm py-1.5 px-2 rounded transition-colors
                ${heading.level === 1 ? 'font-medium' : 'pl-4'}
                ${activeId === heading.id 
                  ? 'text-[#004B8D] bg-[#004B8D]/5 font-medium' 
                  : 'text-slate-600 hover:text-slate-900 hover:bg-slate-50'
                }
              `}
            >
              {heading.text.length > 40 ? heading.text.substring(0, 40) + '...' : heading.text}
            </button>
          </li>
        ))}
      </ul>
    </nav>
  );
};

const LegalPage = () => {
  const { pageType } = useParams();
  const navigate = useNavigate();
  const [content, setContent] = useState({ title: '', content: '', version_id: '', published_at: null });
  const [loading, setLoading] = useState(true);
  const [activeHeadingId, setActiveHeadingId] = useState('');
  const [showMobileToc, setShowMobileToc] = useState(false);
  const [collapsedSections, setCollapsedSections] = useState({});

  const headings = useMemo(() => extractHeadings(content.content), [content.content]);

  useEffect(() => {
    fetchContent();
    // Reset state when page changes
    setCollapsedSections({});
    setActiveHeadingId('');
    // eslint-disable-next-line
  }, [pageType]);

  // Track active heading for TOC highlight
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setActiveHeadingId(entry.target.id);
          }
        });
      },
      { rootMargin: '-100px 0px -80% 0px', threshold: 0 }
    );

    headings.forEach((heading) => {
      const element = document.getElementById(heading.id);
      if (element) observer.observe(element);
    });

    return () => observer.disconnect();
  }, [headings]);

  const fetchContent = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/legal/${pageType}`);
      setContent(res.data);
    } catch (error) {
      console.error('Error fetching legal content:', error);
    } finally {
      setLoading(false);
    }
  };

  const toggleSection = (id) => {
    setCollapsedSections(prev => ({ ...prev, [id]: !prev[id] }));
  };

  const getPageTitle = () => {
    switch (pageType) {
      case 'terms': return 'Terms of Service';
      case 'privacy': return 'Privacy Policy';
      case 'insurance': return 'Insurance Information (Legal)';
      case 'community-guidelines': return 'Community Guidelines';
      case 'refund-policy': return 'Refund Policy';
      default: return 'Legal';
    }
  };

  // Custom components for ReactMarkdown
  const markdownComponents = {
    h1: ({ children }) => <HeadingRenderer level={1}>{children}</HeadingRenderer>,
    h2: ({ children }) => (
      <HeadingRenderer level={2} collapsedSections={collapsedSections} toggleSection={toggleSection}>
        {children}
      </HeadingRenderer>
    ),
    h3: ({ children }) => <HeadingRenderer level={3}>{children}</HeadingRenderer>,
    // Wrap content after H2 for collapsible behavior
    p: ({ children, node }) => {
      return <p className="text-slate-700 leading-[1.7] mb-4">{children}</p>;
    },
    ul: ({ children }) => <ul className="list-disc pl-6 mb-4 space-y-2">{children}</ul>,
    ol: ({ children }) => <ol className="list-decimal pl-6 mb-4 space-y-2">{children}</ol>,
    li: ({ children }) => <li className="text-slate-700 leading-[1.7]">{children}</li>,
    strong: ({ children }) => <strong className="font-semibold text-slate-900">{children}</strong>,
    a: ({ href, children }) => (
      <Link 
        to={href?.startsWith('/') ? href : '#'} 
        className="text-[#004B8D] hover:underline font-medium"
        onClick={(e) => {
          if (!href?.startsWith('/')) {
            e.preventDefault();
            const element = document.getElementById(href?.replace('#', ''));
            if (element) element.scrollIntoView({ behavior: 'smooth' });
          }
        }}
      >
        {children}
      </Link>
    ),
    hr: () => <hr className="my-8 border-slate-200" />,
    blockquote: ({ children }) => (
      <blockquote className="border-l-4 border-[#004B8D]/30 pl-4 py-1 my-4 text-slate-600 italic">
        {children}
      </blockquote>
    ),
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-100">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#004B8D]"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-100">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-50 shadow-sm">
        <div className="max-w-[1200px] mx-auto px-4 md:px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="icon" onClick={() => navigate(-1)} data-testid="back-button">
              <ChevronLeft className="w-5 h-5" />
            </Button>
            <Link to="/" className="flex items-center gap-2">
              <div className="w-10 h-10 bg-[#004B8D] rounded-xl flex items-center justify-center">
                <Car className="w-6 h-6 text-white" />
              </div>
              <span className="font-heading font-bold text-xl text-[#004B8D] hidden sm:inline">HireHub</span>
            </Link>
          </div>
          <div className="flex items-center gap-3">
            {/* Mobile TOC Toggle */}
            <Button 
              variant="outline" 
              size="sm" 
              className="lg:hidden flex items-center gap-2"
              onClick={() => setShowMobileToc(true)}
              data-testid="mobile-toc-toggle"
            >
              <List className="w-4 h-4" />
              <span className="hidden sm:inline">Contents</span>
            </Button>
            {content.version_id && (
              <Badge variant="outline" className="text-xs text-slate-500" data-testid="legal-version-badge">
                {content.version_id}
              </Badge>
            )}
          </div>
        </div>
      </header>

      {/* Mobile TOC Drawer */}
      {showMobileToc && (
        <div className="fixed inset-0 z-50 lg:hidden" data-testid="mobile-toc-drawer">
          <div className="absolute inset-0 bg-black/50" onClick={() => setShowMobileToc(false)} />
          <div className="absolute right-0 top-0 bottom-0 w-[300px] max-w-[85vw] bg-white shadow-xl overflow-y-auto">
            <TableOfContents 
              headings={headings} 
              activeId={activeHeadingId} 
              onClose={() => setShowMobileToc(false)}
              isMobile={true}
            />
          </div>
        </div>
      )}

      {/* Main Content Area */}
      <div className="max-w-[1200px] mx-auto px-4 md:px-6 py-8">
        <div className="flex gap-8">
          {/* Desktop Sticky TOC */}
          <aside className="hidden lg:block w-[240px] flex-shrink-0">
            <div className="sticky top-24" data-testid="desktop-toc">
              <TableOfContents headings={headings} activeId={activeHeadingId} isMobile={false} />
            </div>
          </aside>

          {/* Content Card */}
          <main className="flex-1 min-w-0">
            <article 
              className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6 sm:p-8 md:p-10 lg:p-12"
              style={{ maxWidth: '900px' }}
              data-testid="legal-content-card"
            >
              {/* Document Header */}
              <div className="flex items-start gap-4 mb-6">
                <div className="w-12 h-12 bg-[#004B8D]/10 rounded-xl flex items-center justify-center flex-shrink-0">
                  <FileText className="w-6 h-6 text-[#004B8D]" />
                </div>
                <div>
                  <h1 className="font-heading text-2xl sm:text-3xl font-bold text-slate-900" data-testid="legal-page-title">
                    {content.title || getPageTitle()}
                  </h1>
                  {content.published_at && (
                    <p className="text-sm text-slate-500 mt-2 flex items-center gap-1.5">
                      <Clock className="w-4 h-4" />
                      Last updated: {new Date(content.published_at).toLocaleDateString('en-AU', {
                        day: 'numeric',
                        month: 'long',
                        year: 'numeric'
                      })}
                    </p>
                  )}
                </div>
              </div>

              {/* Divider */}
              <hr className="border-slate-200 mb-8" />
              
              {/* Legal Content */}
              <div 
                className="legal-content text-[16px] sm:text-[17px] leading-[1.7]"
                data-testid="legal-content"
              >
                <ReactMarkdown 
                  remarkPlugins={[remarkGfm]}
                  components={markdownComponents}
                >
                  {content.content || '*No content available*'}
                </ReactMarkdown>
              </div>
            </article>

            {/* Footer Navigation */}
            <nav className="mt-8 flex flex-wrap justify-center gap-4 sm:gap-6 text-sm text-slate-500" data-testid="legal-footer-nav">
              <Link 
                to="/legal/terms" 
                className={`hover:text-[#004B8D] transition-colors ${pageType === 'terms' ? 'text-[#004B8D] font-medium' : ''}`}
              >
                Terms of Service
              </Link>
              <span className="text-slate-300">|</span>
              <Link 
                to="/legal/privacy" 
                className={`hover:text-[#004B8D] transition-colors ${pageType === 'privacy' ? 'text-[#004B8D] font-medium' : ''}`}
              >
                Privacy Policy
              </Link>
              <span className="text-slate-300">|</span>
              <Link 
                to="/legal/insurance" 
                className={`hover:text-[#004B8D] transition-colors ${pageType === 'insurance' ? 'text-[#004B8D] font-medium' : ''}`}
              >
                Insurance Info
              </Link>
              <span className="text-slate-300">|</span>
              <Link 
                to="/legal/community-guidelines" 
                className={`hover:text-[#004B8D] transition-colors ${pageType === 'community-guidelines' ? 'text-[#004B8D] font-medium' : ''}`}
              >
                Community Guidelines
              </Link>
              <span className="text-slate-300">|</span>
              <Link 
                to="/legal/refund-policy" 
                className={`hover:text-[#004B8D] transition-colors ${pageType === 'refund-policy' ? 'text-[#004B8D] font-medium' : ''}`}
              >
                Refund Policy
              </Link>
            </nav>

            {/* Copyright */}
            <p className="text-center text-xs text-slate-400 mt-6">
              © {new Date().getFullYear()} HireHub Australia. ABN 42 139 067 731. All rights reserved.
            </p>
          </main>
        </div>
      </div>
    </div>
  );
};

export default LegalPage;
